/**
 * MOU dashboard — where the paperwork stands across every won deal.
 *
 * Backfilled clients (signature_mode 'legacy') are counted in the status
 * totals but excluded server-side from every speed, conversion and trend
 * figure: they were never sent or signed through this system.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { AlertTriangle, ArrowLeft, Clock, FileSignature, Hourglass, Loader2, RefreshCw, Repeat } from "lucide-react";

import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { MOU_DASHBOARD_URL } from "../../constants/api";
import { fmtCurrency, fmtDate } from "./MOUList";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// Categorical slots 1 and 2 of the validated palette, light / dark steps.
const SERIES = {
  light: { sent: "#2a78d6", signed: "#eb6834", grid: "#e7e5e4", text: "#52514e" },
  dark:  { sent: "#3987e5", signed: "#d95926", grid: "#383835", text: "#c3c2b7" },
};

const monthLabel = (k) => {
  const [y, m] = k.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
};

const Tile = ({ label, value, sub }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
    <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
    <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{value}</p>
    {sub && <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{sub}</p>}
  </div>
);

const Card = ({ title, icon: Icon, children, right }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
    <div className="mb-3 flex items-center justify-between gap-2">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-white">
        {Icon && <Icon className="h-4 w-4 text-gray-400" />} {title}
      </h3>
      {right}
    </div>
    {children}
  </div>
);

// Horizontal proportion bars: one measure, one neutral ink — the label carries identity.
const BarList = ({ rows }) => {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ul className="space-y-2">
      {rows.map((r) => (
        <li key={r.key} title={`${r.label}: ${r.value}`}>
          <div className="flex items-baseline justify-between text-xs">
            <span className="text-gray-600 dark:text-gray-300">{r.label}</span>
            <span className="font-medium tabular-nums text-gray-900 dark:text-white">
              {r.value}{r.extra ? <span className="ml-1 font-normal text-gray-400">{r.extra}</span> : null}
            </span>
          </div>
          <div className="mt-1 h-1.5 rounded-full bg-gray-100 dark:bg-gray-700">
            <div className="h-1.5 rounded-full bg-primary" style={{ width: `${(100 * r.value) / max}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
};

export default function MOUDashboard() {
  const { fetch } = useApiMethods();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTable, setShowTable] = useState(false);
  const dark = typeof document !== "undefined" && document.documentElement.classList.contains("dark");
  const pal = dark ? SERIES.dark : SERIES.light;

  const load = useCallback(() => {
    fetch(MOU_DASHBOARD_URL, {}, setLoading).then((res) => {
      if (res?.status === "success") setD(res.data);
      else ToastMsg("error", res?.message || "Could not load the dashboard");
    });
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const chart = useMemo(() => {
    if (!d) return null;
    return {
      data: {
        labels: d.trend.map((t) => monthLabel(t.month)),
        datasets: [
          { label: "Sent", data: d.trend.map((t) => t.sent), backgroundColor: pal.sent,
            borderRadius: 4, borderSkipped: "start", maxBarThickness: 22,
            borderColor: "transparent", borderWidth: 1 },
          { label: "Signed", data: d.trend.map((t) => t.signed), backgroundColor: pal.signed,
            borderRadius: 4, borderSkipped: "start", maxBarThickness: 22,
            borderColor: "transparent", borderWidth: 1 },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { position: "top", align: "start",
                    labels: { color: pal.text, boxWidth: 10, boxHeight: 10, usePointStyle: true } },
          tooltip: {
            callbacks: {
              afterBody: (items) => {
                const t = d.trend[items[0].dataIndex];
                return t.signed_value ? `Signed value: ${fmtCurrency(t.signed_value)}` : "";
              },
            },
          },
        },
        scales: {
          x: { grid: { display: false }, ticks: { color: pal.text } },
          y: { beginAtZero: true, grid: { color: pal.grid }, border: { display: false },
               ticks: { color: pal.text, precision: 0 } },
        },
      },
    };
  }, [d, pal]);

  if (loading && !d) {
    return (
      <ContainerCard>
        <div className="flex h-64 items-center justify-center gap-2 text-sm text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      </ContainerCard>
    );
  }
  if (!d) return null;

  const k = d.kpis;
  const st = Object.fromEntries(d.by_status.map((s) => [s.status, s]));

  return (
    <ContainerCard>
      <div className="space-y-4 p-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <Button variant="ghost" size="icon" onClick={() => navigate("/mou")} title="Back">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
                <FileSignature className="h-5 w-5 text-primary" /> MOU dashboard
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {d.legacy_signed > 0 && `${d.legacy_signed} pre-existing clients are counted as signed but left out of speed and trend figures.`}
              </p>
            </div>
          </div>
          <Button variant="outline" size="icon" onClick={load} title="Refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Tile label="Open MOUs" value={k.open} sub={`${fmtCurrency(k.open_value)} awaiting signature`} />
          <Tile label="Signed this month" value={k.signed_this_month} sub={fmtCurrency(k.signed_value_this_month)} />
          <Tile label="Avg. days to sign" value={k.avg_days_sent_to_signed ?? "—"}
                sub={k.avg_days_prepared_to_signed != null ? `${k.avg_days_prepared_to_signed} from preparation` : "from sending"} />
          <Tile label="Sign rate" value={k.conversion != null ? `${k.conversion}%` : "—"}
                sub="of sent MOUs that reached a decision" />
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Card title="Sent and signed, last 6 months"
                  right={<button className="text-xs text-primary hover:underline" onClick={() => setShowTable(!showTable)}>
                    {showTable ? "Show chart" : "Show table"}</button>}>
              {showTable ? (
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-gray-500">
                    <th className="py-1">Month</th><th>Prepared</th><th>Sent</th><th>Signed</th><th className="text-right">Signed value</th>
                  </tr></thead>
                  <tbody>{d.trend.map((t) => (
                    <tr key={t.month} className="border-t border-gray-100 text-gray-800 dark:border-gray-700 dark:text-gray-200">
                      <td className="py-1">{monthLabel(t.month)}</td><td>{t.prepared}</td><td>{t.sent}</td><td>{t.signed}</td>
                      <td className="text-right">{fmtCurrency(t.signed_value)}</td>
                    </tr>))}
                  </tbody>
                </table>
              ) : (
                <div className="h-64"><Bar data={chart.data} options={chart.options} /></div>
              )}
            </Card>
          </div>
          <Card title="Where they stand">
            <BarList rows={[
              { key: "np", label: "Not yet prepared", value: d.not_prepared },
              ...["draft", "sent", "signed", "declined", "expired", "cancelled"].map((s) => ({
                key: s, label: st[s]?.label || s, value: st[s]?.count || 0,
                extra: st[s]?.value ? fmtCurrency(st[s].value) : "",
              })),
            ]} />
          </Card>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Card title="Sent, waiting for signature" icon={Clock}>
            <BarList rows={Object.entries(d.aging).map(([b, v]) => ({ key: b, label: `${b} days`, value: v }))} />
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              {k.sent_unopened} sent MOU{k.sent_unopened === 1 ? "" : "s"} not opened by the client yet.
            </p>
          </Card>
          <Card title="Expiring within 7 days" icon={Hourglass}>
            {d.expiring.length === 0 ? <p className="text-xs text-gray-400">Nothing expiring</p> : (
              <ul className="space-y-2">{d.expiring.map((r) => (
                <li key={r.id}>
                  <button className="w-full text-left" onClick={() => navigate(`/mou/${r.id}`)}>
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <span className="truncate font-medium text-gray-900 hover:text-primary dark:text-white">{r.org_name}</span>
                      <span className={r.days_left <= 2 ? "flex items-center gap-1 text-red-600 dark:text-red-400" : "text-gray-500"}>
                        {r.days_left <= 2 && <AlertTriangle className="h-3 w-3" />}
                        {r.days_left === 0 ? "today" : `${r.days_left}d`}
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-500">{r.mou_number} · {r.status_label} · {fmtCurrency(r.contract_value)}</p>
                  </button>
                </li>))}
              </ul>
            )}
          </Card>
          <Card title="Renewals due (60 days)" icon={Repeat}>
            {d.renewals_due.length === 0 ? <p className="text-xs text-gray-400">No contracts ending soon</p> : (
              <ul className="space-y-2">{d.renewals_due.map((r) => (
                <li key={r.id}>
                  <button className="w-full text-left" onClick={() => navigate(`/mou/${r.id}`)}>
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <span className="truncate font-medium text-gray-900 hover:text-primary dark:text-white">{r.org_name}</span>
                      <span className="text-gray-500">{r.days_left < 0 ? `ended ${-r.days_left}d ago` : `${r.days_left}d`}</span>
                    </div>
                    <p className="text-[11px] text-gray-500">
                      Ends {fmtDate(r.contract_end_date)} · {r.renewal_open ? "renewal in progress" : "no renewal yet"}
                    </p>
                  </button>
                </li>))}
              </ul>
            )}
          </Card>
        </div>

        <Card title="By owner">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-xs">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400">
                  <th className="py-1.5">Owner</th><th>Prepared</th><th>Sent</th><th>Open</th><th>Signed</th>
                  <th>Sign rate</th><th>Avg. days</th><th className="text-right">Signed value</th>
                </tr>
              </thead>
              <tbody>
                {d.owners.length === 0 && (
                  <tr><td colSpan={8} className="py-4 text-center text-gray-400">No MOUs prepared through the system yet</td></tr>
                )}
                {d.owners.map((o) => (
                  <tr key={o.owner_id ?? "none"} className="border-t border-gray-100 text-gray-800 dark:border-gray-700 dark:text-gray-200">
                    <td className="py-1.5 font-medium">{o.owner_name}</td>
                    <td>{o.prepared}</td><td>{o.sent}</td><td>{o.open}</td><td>{o.signed}</td>
                    <td>{o.conversion != null ? `${o.conversion}%` : "—"}</td>
                    <td>{o.avg_days_to_sign ?? "—"}</td>
                    <td className="text-right">{fmtCurrency(o.signed_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

      </div>
    </ContainerCard>
  );
}
